// Shared pattern definitions and tier swatches for the
// ReportProficiencyGraph. The same visual language (red diagonal stripes,
// orange dot grid, pink reverse-diagonal stripes) is used in three places:
//
//   1. The bar fills inside the chart (via `PatternDefs` + the
//      `PROFICIENCY_PALETTE` `url(#...)` references).
//   2. The legend swatches above the chart.
//   3. The tooltip swatches.
//
// The chart bars use a larger pattern tile so the print is visible on tall
// bars; the swatches use a smaller tile so the print reads clearly at 16×16.

import type { ReactNode } from 'react';

const TIER_BACKGROUNDS: string[] = ['#FE0000', '#EF7000', '#D837AF'];
export const FALLBACK_FILL = '#22A5A5';

// ── Bar (chart) pattern defs ──────────────────────────────────────────────────

export function PatternDefs() {
  return (
    <defs>
      {/* Tier 1 (lowest): red bg + white diagonal stripes (TR → BL) */}
      <pattern
        id="pattern-tier-1"
        patternUnits="userSpaceOnUse"
        width="9.317"
        height="9.317"
        patternTransform="rotate(45)"
      >
        <rect width="9.317" height="9.317" fill={TIER_BACKGROUNDS[0]} />
        <line x1="0" y1="0" x2="0" y2="9.317" stroke="white" strokeWidth="5" />
      </pattern>

      {/* Tier 2: orange bg + white dot grid */}
      <pattern
        id="pattern-tier-2"
        patternUnits="userSpaceOnUse"
        width="7"
        height="7"
      >
        <rect width="7" height="7" fill={TIER_BACKGROUNDS[1]} />
        <circle cx="3.5" cy="3.5" r="1.5" fill="white" />
      </pattern>

      {/* Tier 3: pink bg + white diagonal stripes (TL → BR) */}
      <pattern
        id="pattern-tier-3"
        patternUnits="userSpaceOnUse"
        width="9.317"
        height="9.317"
        patternTransform="rotate(-45)"
      >
        <rect width="9.317" height="9.317" fill={TIER_BACKGROUNDS[2]} />
        <line x1="0" y1="0" x2="0" y2="9.317" stroke="white" strokeWidth="5" />
      </pattern>
    </defs>
  );
}

// Ordered palette — assigned by index from the API's proficiency order
// (lowest tier → highest tier).
export const PROFICIENCY_PALETTE: string[] = [
  'url(#pattern-tier-1)',
  'url(#pattern-tier-2)',
  'url(#pattern-tier-3)',
];

export function getFillForIndex(index: number): string {
  return PROFICIENCY_PALETTE[index] ?? FALLBACK_FILL;
}

// ── Tier swatches (legend / tooltip) ──────────────────────────────────────────

// Smaller pattern tiles so the print reads clearly at 16×16. Each swatch is a
// single <svg> with its own scoped <defs><pattern> filled into one <rect>.

interface TierSwatchBaseProps {
  ariaLabel?: string;
  patternId: string;
}

function SwatchSvg({
  ariaLabel,
  children,
  patternId,
}: TierSwatchBaseProps & { children: ReactNode }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 16 16"
      role={ariaLabel ? 'img' : undefined}
      aria-label={ariaLabel}
      className="rounded-sm"
    >
      <defs>{children}</defs>
      <rect width="16" height="16" rx="3" fill={`url(#${patternId})`} />
    </svg>
  );
}

interface TierSwatchProps {
  index: number;
  ariaLabel?: string;
}

export function TierSwatch({ index, ariaLabel }: TierSwatchProps) {
  if (index === 0) {
    const id = 'swatch-tier-1';
    return (
      <SwatchSvg ariaLabel={ariaLabel} patternId={id}>
        <pattern
          id={id}
          patternUnits="userSpaceOnUse"
          width="4"
          height="4"
          patternTransform="rotate(45)"
        >
          <rect width="4" height="4" fill={TIER_BACKGROUNDS[0]} />
          <line x1="0" y1="0" x2="0" y2="4" stroke="white" strokeWidth="2" />
        </pattern>
      </SwatchSvg>
    );
  }

  if (index === 1) {
    const id = 'swatch-tier-2';
    return (
      <SwatchSvg ariaLabel={ariaLabel} patternId={id}>
        <pattern id={id} patternUnits="userSpaceOnUse" width="4" height="4">
          <rect width="4" height="4" fill={TIER_BACKGROUNDS[1]} />
          <circle cx="2" cy="2" r="1" fill="white" />
        </pattern>
      </SwatchSvg>
    );
  }

  if (index === 2) {
    const id = 'swatch-tier-3';
    return (
      <SwatchSvg ariaLabel={ariaLabel} patternId={id}>
        <pattern
          id={id}
          patternUnits="userSpaceOnUse"
          width="4"
          height="4"
          patternTransform="rotate(-45)"
        >
          <rect width="4" height="4" fill={TIER_BACKGROUNDS[2]} />
          <line x1="0" y1="0" x2="0" y2="4" stroke="white" strokeWidth="2" />
        </pattern>
      </SwatchSvg>
    );
  }

  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      role={ariaLabel ? 'img' : undefined}
      aria-label={ariaLabel}
      className="rounded-sm"
    >
      <rect width="16" height="16" rx="3" fill={FALLBACK_FILL} />
    </svg>
  );
}
